import React, { useState } from "react";
import { Plus, Trash2, ChevronDown, ChevronUp, Info } from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Badge } from "./ui/badge";

// Available indicators with their configurable parameters
const AVAILABLE_INDICATORS = [
  {
    type: "EMA",
    label: "EMA (Exponential Moving Average)",
    description: "Tracks price trend by weighting recent prices more heavily.",
    color: "text-blue-400",
    params: [{ key: "period", label: "Period", type: "number", min: 2, step: 1, default: 9 }],
  },
  {
    type: "RSI",
    label: "RSI (Relative Strength Index)",
    description: "Measures momentum to identify overbought/oversold conditions.",
    color: "text-purple-400",
    params: [{ key: "period", label: "Period", type: "number", min: 2, step: 1, default: 14 }],
  },
  {
    type: "ATR",
    label: "ATR (Average True Range)",
    description: "Measures market volatility over a period.",
    color: "text-yellow-400",
    params: [{ key: "period", label: "Period", type: "number", min: 1, step: 1, default: 14 }],
  },
  {
    type: "SUPERTREND",
    label: "Supertrend",
    description: "Trend-following indicator using ATR to signal direction.",
    color: "text-green-400",
    params: [
      { key: "period", label: "Period", type: "number", min: 1, step: 1, default: 10 },
      { key: "multiplier", label: "Multiplier", type: "number", min: 0.1, step: 0.1, default: 3 },
    ],
  },
  {
    type: "KAMA",
    label: "KAMA (Kaufman Adaptive MA)",
    description: "Adapts its speed to market noise for cleaner trend detection.",
    color: "text-orange-400",
    params: [
      { key: "period", label: "Period", type: "number", min: 2, step: 1, default: 10 },
      { key: "fast", label: "Fast Period", type: "number", min: 1, step: 1, default: 2 },
      { key: "slow", label: "Slow Period", type: "number", min: 2, step: 1, default: 30 },
    ],
  },
  {
    type: "BBANDS",
    label: "Bollinger Bands",
    description: "Volatility bands placed above and below a moving average.",
    color: "text-cyan-400",
    params: [
      { key: "period", label: "Period", type: "number", min: 2, step: 1, default: 20 },
      { key: "std_dev", label: "Std Dev", type: "number", min: 0.1, step: 0.1, default: 2 },
    ],
  },
  {
    type: "MACD",
    label: "MACD",
    description: "Shows relationship between two EMAs to signal momentum shifts.",
    color: "text-pink-400",
    params: [
      { key: "fast", label: "Fast Period", type: "number", min: 2, step: 1, default: 12 },
      { key: "slow", label: "Slow Period", type: "number", min: 2, step: 1, default: 26 },
      { key: "signal", label: "Signal Period", type: "number", min: 1, step: 1, default: 9 },
    ],
  },
];

// Condition operators per indicator type
const CONDITION_OPERATORS = {
  EMA: ["price_above", "price_below", "crosses_above", "crosses_below"],
  RSI: ["above", "below", "crosses_above", "crosses_below"],
  ATR: ["above", "below"],
  SUPERTREND: ["is_bullish", "is_bearish", "flips_bullish", "flips_bearish"],
  KAMA: ["price_above", "price_below", "crosses_above", "crosses_below"],
  BBANDS: ["price_above_upper", "price_below_lower", "price_inside"],
  MACD: ["crosses_above_signal", "crosses_below_signal", "above_zero", "below_zero"],
};

const OPERATOR_LABELS = {
  price_above: "Price is above",
  price_below: "Price is below",
  crosses_above: "Price crosses above",
  crosses_below: "Price crosses below",
  above: "Value is above",
  below: "Value is below",
  is_bullish: "Is bullish",
  is_bearish: "Is bearish",
  flips_bullish: "Flips to bullish",
  flips_bearish: "Flips to bearish",
  price_above_upper: "Price above upper band",
  price_below_lower: "Price below lower band",
  price_inside: "Price inside bands",
  crosses_above_signal: "MACD crosses above signal",
  crosses_below_signal: "MACD crosses below signal",
  above_zero: "MACD above zero",
  below_zero: "MACD below zero",
};

function IndicatorParamEditor({ indicatorDef, params, onChange }) {
  return (
    <div className="grid grid-cols-2 gap-2 mt-2">
      {indicatorDef.params.map((param) => (
        <div key={param.key} className="space-y-1">
          <Label className="text-xs text-zinc-400">{param.label}</Label>
          <Input
            type="number"
            min={param.min}
            step={param.step}
            value={params?.[param.key] ?? param.default}
            onChange={(e) => onChange(param.key, Number(e.target.value))}
            className="h-7 border-zinc-700 bg-zinc-900 text-xs text-white"
          />
        </div>
      ))}
    </div>
  );
}

function ConditionRow({ condition, activeIndicators, onUpdate, onRemove }) {
  const indicatorOptions = activeIndicators.filter(
    (ind) => CONDITION_OPERATORS[ind.type]?.length > 0
  );
  const selectedIndicator = activeIndicators.find((i) => i.type === condition.indicator);
  const operators = CONDITION_OPERATORS[condition.indicator] || [];
  const needsThreshold = ["above", "below"].includes(condition.operator);

  return (
    <div className="flex items-center gap-2 rounded-md border border-zinc-800 bg-zinc-900 px-3 py-2">
      {/* Indicator selector */}
      <select
        value={condition.indicator || ""}
        onChange={(e) => onUpdate({ ...condition, indicator: e.target.value, operator: CONDITION_OPERATORS[e.target.value]?.[0] || "" })}
        className="h-7 rounded border border-zinc-700 bg-zinc-800 px-2 text-xs text-white"
      >
        <option value="">Pick indicator</option>
        {indicatorOptions.map((ind) => (
          <option key={ind.type} value={ind.type}>{ind.type}</option>
        ))}
      </select>

      {/* Operator selector */}
      {condition.indicator && (
        <select
          value={condition.operator || ""}
          onChange={(e) => onUpdate({ ...condition, operator: e.target.value })}
          className="h-7 rounded border border-zinc-700 bg-zinc-800 px-2 text-xs text-white"
        >
          {operators.map((op) => (
            <option key={op} value={op}>{OPERATOR_LABELS[op] || op}</option>
          ))}
        </select>
      )}

      {/* Threshold value for above/below operators */}
      {needsThreshold && (
        <Input
          type="number"
          placeholder="value"
          value={condition.threshold ?? ""}
          onChange={(e) => onUpdate({ ...condition, threshold: Number(e.target.value) })}
          className="h-7 w-20 border-zinc-700 bg-zinc-800 text-xs text-white"
        />
      )}

      <button
        onClick={onRemove}
        className="ml-auto text-zinc-500 hover:text-red-400 transition-colors"
      >
        <Trash2 className="h-3.5 w-3.5" />
      </button>
    </div>
  );
}

export default function VisualStrategyBuilder({ indicators = [], entryConditions = {}, exitConditions = {}, onChange }) {
  const [expandedIndicator, setExpandedIndicator] = useState(null);
  const [showIndicatorPicker, setShowIndicatorPicker] = useState(false);

  const entryRules = entryConditions?.rules || [];
  const exitRules = exitConditions?.rules || [];
  const logic = entryConditions?.logic || "AND";
  const exitLogic = exitConditions?.logic || "OR";

  // Normalize indicators to objects
  const normalizedIndicators = indicators.map((ind) =>
    typeof ind === "string" ? { type: ind } : ind
  );

  function addIndicator(type) {
    const def = AVAILABLE_INDICATORS.find((i) => i.type === type);
    if (!def || normalizedIndicators.find((i) => i.type === type)) return;
    const defaultParams = {};
    def.params.forEach((p) => { defaultParams[p.key] = p.default; });
    const updated = [...normalizedIndicators, { type, ...defaultParams }];
    onChange({ indicators: updated, entryConditions, exitConditions });
    setShowIndicatorPicker(false);
    setExpandedIndicator(type);
  }

  function removeIndicator(type) {
    const updated = normalizedIndicators.filter((i) => i.type !== type);
    // Also remove conditions referencing this indicator
    const newEntry = { ...entryConditions, rules: entryRules.filter((r) => r.indicator !== type) };
    const newExit = { ...exitConditions, rules: exitRules.filter((r) => r.indicator !== type) };
    onChange({ indicators: updated, entryConditions: newEntry, exitConditions: newExit });
  }

  function updateIndicatorParam(type, key, value) {
    const updated = normalizedIndicators.map((ind) =>
      ind.type === type ? { ...ind, [key]: value } : ind
    );
    onChange({ indicators: updated, entryConditions, exitConditions });
  }

  function addConditionRule(side) {
    const newRule = { indicator: normalizedIndicators[0]?.type || "", operator: "", threshold: null };
    if (side === "entry") {
      onChange({ indicators: normalizedIndicators, entryConditions: { ...entryConditions, rules: [...entryRules, newRule] }, exitConditions });
    } else {
      onChange({ indicators: normalizedIndicators, entryConditions, exitConditions: { ...exitConditions, rules: [...exitRules, newRule] } });
    }
  }

  function updateConditionRule(side, index, updated) {
    if (side === "entry") {
      const rules = entryRules.map((r, i) => (i === index ? updated : r));
      onChange({ indicators: normalizedIndicators, entryConditions: { ...entryConditions, rules }, exitConditions });
    } else {
      const rules = exitRules.map((r, i) => (i === index ? updated : r));
      onChange({ indicators: normalizedIndicators, entryConditions, exitConditions: { ...exitConditions, rules } });
    }
  }

  function removeConditionRule(side, index) {
    if (side === "entry") {
      const rules = entryRules.filter((_, i) => i !== index);
      onChange({ indicators: normalizedIndicators, entryConditions: { ...entryConditions, rules }, exitConditions });
    } else {
      const rules = exitRules.filter((_, i) => i !== index);
      onChange({ indicators: normalizedIndicators, entryConditions, exitConditions: { ...exitConditions, rules } });
    }
  }

  function setLogic(side, value) {
    if (side === "entry") {
      onChange({ indicators: normalizedIndicators, entryConditions: { ...entryConditions, logic: value }, exitConditions });
    } else {
      onChange({ indicators: normalizedIndicators, entryConditions, exitConditions: { ...exitConditions, logic: value } });
    }
  }

  const unusedIndicators = AVAILABLE_INDICATORS.filter(
    (def) => !normalizedIndicators.find((i) => i.type === def.type)
  );

  return (
    <div className="space-y-5">

      {/* ── INDICATORS ── */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <Label className="text-sm font-semibold text-white">Indicators</Label>
          <Button
            type="button"
            size="sm"
            variant="outline"
            className="h-7 border-zinc-700 text-xs"
            onClick={() => setShowIndicatorPicker((v) => !v)}
            disabled={unusedIndicators.length === 0}
          >
            <Plus className="h-3 w-3 mr-1" />
            Add Indicator
          </Button>
        </div>

        {/* Indicator picker dropdown */}
        {showIndicatorPicker && (
          <div className="mb-3 rounded-md border border-zinc-700 bg-zinc-900 p-2 grid grid-cols-1 gap-1">
            {unusedIndicators.map((def) => (
              <button
                key={def.type}
                type="button"
                onClick={() => addIndicator(def.type)}
                className="flex items-start gap-3 rounded-md px-3 py-2 text-left hover:bg-zinc-800 transition-colors"
              >
                <span className={`font-mono text-sm font-bold ${def.color}`}>{def.type}</span>
                <span className="text-xs text-zinc-400">{def.description}</span>
              </button>
            ))}
          </div>
        )}

        {/* Active indicators */}
        {normalizedIndicators.length === 0 ? (
          <div className="rounded-md border border-dashed border-zinc-700 p-4 text-center text-xs text-zinc-500">
            No indicators added yet. Click "Add Indicator" to get started.
          </div>
        ) : (
          <div className="space-y-2">
            {normalizedIndicators.map((ind) => {
              const def = AVAILABLE_INDICATORS.find((d) => d.type === ind.type);
              const isExpanded = expandedIndicator === ind.type;
              return (
                <div key={ind.type} className="rounded-md border border-zinc-800 bg-zinc-950">
                  <div
                    className="flex items-center gap-3 px-3 py-2 cursor-pointer select-none"
                    onClick={() => setExpandedIndicator(isExpanded ? null : ind.type)}
                  >
                    <span className={`font-mono text-sm font-bold ${def?.color || "text-white"}`}>{ind.type}</span>
                    <span className="text-xs text-zinc-500 flex-1">{def?.label}</span>
                    {def?.params.length > 0 && (
                      <span className="text-xs text-zinc-600">
                        {def.params.map((p) => `${p.label}: ${ind[p.key] ?? p.default}`).join(" · ")}
                      </span>
                    )}
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); removeIndicator(ind.type); }}
                      className="text-zinc-600 hover:text-red-400 transition-colors"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                    {isExpanded ? <ChevronUp className="h-3.5 w-3.5 text-zinc-500" /> : <ChevronDown className="h-3.5 w-3.5 text-zinc-500" />}
                  </div>

                  {isExpanded && def?.params.length > 0 && (
                    <div className="border-t border-zinc-800 px-3 pb-3">
                      <IndicatorParamEditor
                        indicatorDef={def}
                        params={ind}
                        onChange={(key, value) => updateIndicatorParam(ind.type, key, value)}
                      />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* ── ENTRY CONDITIONS ── */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            <Label className="text-sm font-semibold text-white">Entry Conditions</Label>
            <Badge variant="outline" className="text-[10px] text-green-400 border-green-400/30">BUY when</Badge>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-xs text-zinc-500">Match:</span>
            {["AND", "OR"].map((val) => (
              <button
                key={val}
                type="button"
                onClick={() => setLogic("entry", val)}
                className={`rounded px-2 py-0.5 text-xs font-medium transition-colors ${logic === val ? "bg-blue-600 text-white" : "bg-zinc-800 text-zinc-400 hover:bg-zinc-700"}`}
              >
                {val}
              </button>
            ))}
            <Button
              type="button"
              size="sm"
              variant="outline"
              className="h-6 border-zinc-700 text-xs"
              onClick={() => addConditionRule("entry")}
              disabled={normalizedIndicators.length === 0}
            >
              <Plus className="h-3 w-3 mr-1" />
              Rule
            </Button>
          </div>
        </div>

        {entryRules.length === 0 ? (
          <div className="rounded-md border border-dashed border-zinc-700 p-3 text-center text-xs text-zinc-500">
            {normalizedIndicators.length === 0
              ? "Add indicators above first, then define entry rules."
              : 'No entry rules yet. Click "+ Rule" to add a condition.'}
          </div>
        ) : (
          <div className="space-y-2">
            {entryRules.map((rule, i) => (
              <React.Fragment key={i}>
                {i > 0 && (
                  <div className="text-center text-[10px] font-semibold text-zinc-600">{logic}</div>
                )}
                <ConditionRow
                  condition={rule}
                  activeIndicators={normalizedIndicators}
                  onUpdate={(updated) => updateConditionRule("entry", i, updated)}
                  onRemove={() => removeConditionRule("entry", i)}
                />
              </React.Fragment>
            ))}
          </div>
        )}
      </div>

      {/* ── EXIT CONDITIONS ── */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            <Label className="text-sm font-semibold text-white">Exit Conditions</Label>
            <Badge variant="outline" className="text-[10px] text-red-400 border-red-400/30">SELL when</Badge>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-xs text-zinc-500">Match:</span>
            {["AND", "OR"].map((val) => (
              <button
                key={val}
                type="button"
                onClick={() => setLogic("exit", val)}
                className={`rounded px-2 py-0.5 text-xs font-medium transition-colors ${exitLogic === val ? "bg-blue-600 text-white" : "bg-zinc-800 text-zinc-400 hover:bg-zinc-700"}`}
              >
                {val}
              </button>
            ))}
            <Button
              type="button"
              size="sm"
              variant="outline"
              className="h-6 border-zinc-700 text-xs"
              onClick={() => addConditionRule("exit")}
              disabled={normalizedIndicators.length === 0}
            >
              <Plus className="h-3 w-3 mr-1" />
              Rule
            </Button>
          </div>
        </div>

        {exitRules.length === 0 ? (
          <div className="rounded-md border border-dashed border-zinc-700 p-3 text-center text-xs text-zinc-500">
            {normalizedIndicators.length === 0
              ? "Add indicators above first, then define exit rules."
              : 'No exit rules yet. Click "+ Rule" to add a condition.'}
          </div>
        ) : (
          <div className="space-y-2">
            {exitRules.map((rule, i) => (
              <React.Fragment key={i}>
                {i > 0 && (
                  <div className="text-center text-[10px] font-semibold text-zinc-600">{exitLogic}</div>
                )}
                <ConditionRow
                  condition={rule}
                  activeIndicators={normalizedIndicators}
                  onUpdate={(updated) => updateConditionRule("exit", i, updated)}
                  onRemove={() => removeConditionRule("exit", i)}
                />
              </React.Fragment>
            ))}
          </div>
        )}
      </div>

      {/* ── SUMMARY ── */}
      {(normalizedIndicators.length > 0 || entryRules.length > 0) && (
        <div className="rounded-md border border-zinc-800 bg-zinc-950/60 px-3 py-3">
          <div className="flex items-center gap-1.5 mb-2">
            <Info className="h-3.5 w-3.5 text-zinc-500" />
            <span className="text-xs font-medium text-zinc-400">Strategy Preview</span>
          </div>
          <div className="space-y-1 text-xs text-zinc-500">
            <div>
              <span className="text-zinc-400">Indicators: </span>
              {normalizedIndicators.length > 0
                ? normalizedIndicators.map((i) => i.type).join(", ")
                : "None"}
            </div>
            <div>
              <span className="text-zinc-400">Entry: </span>
              {entryRules.length > 0
                ? entryRules.map((r) => `${r.indicator} ${OPERATOR_LABELS[r.operator] || r.operator}${r.threshold != null ? ` ${r.threshold}` : ""}`).join(` ${logic} `)
                : "No conditions defined"}
            </div>
            <div>
              <span className="text-zinc-400">Exit: </span>
              {exitRules.length > 0
                ? exitRules.map((r) => `${r.indicator} ${OPERATOR_LABELS[r.operator] || r.operator}${r.threshold != null ? ` ${r.threshold}` : ""}`).join(` ${exitLogic} `)
                : "No conditions defined"}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
